import {
    Directive,
    OnInit,
    TemplateRef,
    ViewContainerRef,
    Input,
    OnDestroy
} from '@angular/core';
import { UserService } from 'services';
import { UserToken, UserClaimType } from 'models';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * Directive displayForClaims
 *
 * Displays an element to which it applies for Claims listed in array, hides for others
 *
 * Usage: <div *displayForClaims="['IsSuperhogVerified', 'CompletedVerificationJourney']"><div>
 */
@Directive({ selector: '[displayForClaims]' })
export class DisplayForClaimsDirective implements OnInit, OnDestroy {
    protected claimsToDisplay: UserClaimType[] = [];
    protected subscription: Subscription;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private userService: UserService
    ) { }

    @Input()
    set displayForClaims(Claims: UserClaimType[]) {
        if (!Array.isArray(Claims))
            throw new Error('displayForClaims directive must contain an array');
        this.claimsToDisplay = Claims;
    }

    ngOnInit() {
        this.subscription = this.userService.userTokenClaims$
            .pipe(tap(_ => this.viewContainer.clear()))
            .subscribe((userClaims: UserClaimType[]) => {
                if (!userClaims.length || userClaims.filter(claim => -1 !== this.claimsToDisplay.indexOf(claim)).length > 0 ) {
                    // If user is permission granted -> displaying element
                    this.viewContainer.createEmbeddedView(this.templateRef);
                }
            });
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }
}


/**
 * Directive hideForClaims
 *
 * @ToDo fix it "Cannot read property 'Claims' of null" when user has no Claim
 *
 * Hides an element to which it applies for Claims listed in array, displays for others
 *
 * Usage: <div *hideForClaims="['Platform', 'Guest']"><div>
 */
@Directive({ selector: '[hideForClaims]' })
export class HideForClaimsDirective implements OnInit, OnDestroy {
    protected ClaimsToHide: UserClaimType[] = [];
    protected subscription: Subscription;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private userService: UserService
    ) { }

    @Input()
    set hideForClaims(claims: UserClaimType[]) {
        if (!Array.isArray(claims)) throw new Error('hideForClaims directive must contain an array');
        this.ClaimsToHide = claims;
    }

    ngOnInit() {
        this.subscription = this.userService.userTokenClaims$
            .pipe(tap(_ => this.viewContainer.clear()))
            .subscribe((userClaims: UserClaimType[]) => {
                if (
                    (!userClaims.length) || userClaims.filter(claim => -1 !== this.ClaimsToHide.indexOf(claim)).length === 0
                ) {
                    this.viewContainer.createEmbeddedView(this.templateRef);
                }
            });
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }
}

/**
 * Directive unauthenticatedOnly
 *
 * Displays an element to unauthenticated users only
 *
 * Usage: <div *unauthenticatedOnly><div>
 */
@Directive({ selector: '[unauthenticatedOnly]' })
export class UnauthenticatedOnlyDirective implements OnInit, OnDestroy {
    protected subscription: Subscription;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private userService: UserService
    ) {}

    ngOnInit() {
        this.subscription = this.userService.userToken$
            .pipe(tap(_ => this.viewContainer.clear()))
            .subscribe((user: UserToken) => {
                if (!user) {
                    this.viewContainer.createEmbeddedView(this.templateRef);
                }
            });
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }
}

/**
 * Directive authenticatedOnly
 *
 * Displays an element to authenticated users only
 *
 * Usage: <div *authenticatedOnly><div>
 */
@Directive({ selector: '[authenticatedOnly]' })
export class AuthenticatedOnlyDirective implements OnInit, OnDestroy {
    protected subscription: Subscription;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private userService: UserService
    ) {}

    ngOnInit() {
        this.subscription = this.userService.userToken$
            .pipe(tap(_ => this.viewContainer.clear()))
            .subscribe((user: UserToken) => {
                if (user) {
                    this.viewContainer.createEmbeddedView(this.templateRef);
                }
            });
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }
}

/**
 * Directive switchedFromAdminOnly
 *
 * Displays an element to admins taking over user accounts
 *
 * Usage: <div *switchedFromAdminOnly><div>
 */
@Directive({ selector: '[switchedFromAdminOnly]' })
export class SwitchedFromAdminOnlyDirective implements OnInit, OnDestroy {
    protected subscription: Subscription;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private userService: UserService
    ) { }

    ngOnInit() {
        const switchedFromAdmin = this.userService.userToken.switchedFromAdmin;
        if (switchedFromAdmin) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }
}


/**
 * Directive notSwitchedFromAdminOnly
 *
 * Displays an element to admins taking over user accounts
 *
 * Usage: <div *notSwitchedFromAdminOnly><div>
 */
@Directive({ selector: '[notSwitchedFromAdminOnly]' })
export class NotSwitchedFromAdminOnlyDirective implements OnInit, OnDestroy {
    protected subscription: Subscription;

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private userService: UserService
    ) { }

    ngOnInit() {
        const switchedFromAdmin = this.userService.userToken.switchedFromAdmin;
        if (!switchedFromAdmin) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }
}